import { Star, CheckCircle2 } from "lucide-react";

type Status = "removed" | "submitted" | "flagged" | "watching";

type ReviewCard = {
  author: string;
  stars: number;
  body: string;
  violation: string;
  status: Status;
  emphasized?: boolean;
};

const reviews: ReviewCard[] = [
  {
    author: "Anonymous",
    stars: 1,
    body: "DM me for a discount code and I'll update my review…",
    violation: "Incentivized review",
    status: "removed",
  },
  {
    author: "J. Patel",
    stars: 1,
    body: "Competitor's product is way better, buy from them instead.",
    violation: "Competitor promotion",
    status: "submitted",
  },
  {
    author: "M. Chen",
    stars: 2,
    body: "Seller was rude when I emailed them — terrible service.",
    violation: "Seller feedback on product listing",
    status: "flagged",
    emphasized: true,
  },
  {
    author: "K. Rivera",
    stars: 1,
    body: "This review contains profanity and personal attacks…",
    violation: "Inappropriate content",
    status: "watching",
  },
];

const statusStyles: Record<Status, string> = {
  removed: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900",
  submitted: "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900",
  flagged: "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900",
  watching: "bg-slate-50 text-slate-600 ring-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-800",
};

const statusLabel: Record<Status, string> = {
  removed: "Removed",
  submitted: "Submitted",
  flagged: "Flagged",
  watching: "Watching",
};

function Stars({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={
            i < count
              ? "h-3.5 w-3.5 fill-amber-400 text-amber-400"
              : "h-3.5 w-3.5 text-slate-200 dark:text-slate-700"
          }
        />
      ))}
    </div>
  );
}

export function ReviewRemovalVisual() {
  return (
    <div className="relative w-full">
      <div
        aria-hidden
        className="absolute inset-0 -z-10 flex justify-center opacity-60"
      >
        <div className="h-full w-[70%] bg-[repeating-linear-gradient(to_bottom,transparent_0,transparent_7px,rgba(16,185,129,0.12)_7px,rgba(16,185,129,0.12)_8px)]" />
      </div>
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-gradient-to-b from-emerald-50/40 via-transparent to-transparent"
      />

      <div className="flex flex-col gap-3 px-2 py-6 sm:px-6">
        {reviews.map((r, idx) => (
          <div
            key={r.author + idx}
            className={[
              "rounded-2xl border bg-white p-4 shadow-sm transition dark:bg-slate-900",
              r.emphasized
                ? "border-emerald-300 shadow-emerald-100 ring-1 ring-emerald-200 dark:border-emerald-800 dark:shadow-none dark:ring-emerald-900"
                : "border-slate-200 dark:border-slate-800",
              r.status === "watching" ? "opacity-60" : "",
            ].join(" ")}
            style={{
              transform: `translateX(${idx % 2 === 0 ? "0" : "12px"})`,
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Stars count={r.stars} />
                  <span className="text-xs text-slate-500 dark:text-slate-400">{r.author}</span>
                </div>
                <p className="mt-1.5 truncate text-sm text-slate-700 dark:text-slate-300">
                  {r.body}
                </p>
                <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <span className="inline-block h-1 w-1 rounded-full bg-slate-400 dark:bg-slate-500" />
                  {r.violation}
                </div>
              </div>
              <span
                className={[
                  "inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
                  statusStyles[r.status],
                ].join(" ")}
              >
                {r.status === "removed" && (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                )}
                {statusLabel[r.status]}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
